import React, { Fragment, useState } from 'react';
import {
  Identifier,
  Record as RaRecord,
  useDataProvider,
  useListContext,
  useNotify,
} from 'react-admin';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';

import { PermissionsProvider } from 'providers/permissionsProvider';
import {
  DateRangeFields,
  isDateRangeSet,
  markedAsPaidMessage,
} from './helpers';
import { MarkAsPaidConfirm } from './MarkAsPaidConfirm';
import { MarkAsPaidTarget, useMarkAsPaidPrompt } from './useMarkAsPaidPrompt';

/** Counts every *-paid batch endpoint returns. */
export type MarkAsPaidResult = {
  markedCount: number;
  skippedCount: number;
};

export type MarkAsPaidButtonProps<Body> = MarkAsPaidTarget<Body> & {
  /** Permission flag; without it the button is not rendered. */
  permission: string;
  /** Filter keys that must both be set before the action is enabled. */
  dateRange: DateRangeFields;
  /** Tooltip lines shown while the date range is incomplete. */
  tooltipLines: string[];
  /** Builds the request body from the selected ids. */
  buildBody: (selectedIds: Identifier[]) => Body | Promise<Body>;
  label?: string;
};

/**
 * Selection-bar action marking every selected row as paid in one request,
 * once the user confirms the contractor summary of the selection.
 */
export const MarkAsPaidButton = <Body,>({
  permission,
  dateRange,
  tooltipLines,
  resource,
  buildBody,
  warningsFor,
  label = 'Mark as Paid',
}: MarkAsPaidButtonProps<Body>) => {
  const { filterValues, selectedIds, onUnselectItems, refetch } =
    useListContext();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const { hasPermission } = PermissionsProvider.useContainer();

  const [isSaving, setIsSaving] = useState(false);
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);

  const prompt = useMarkAsPaidPrompt<Body>(
    { resource, warningsFor },
    async body => {
      setIsSaving(true);
      try {
        const { data } = await dataProvider.create<RaRecord & MarkAsPaidResult>(
          resource,
          { data: body },
        );
        notify(markedAsPaidMessage(data.markedCount, data.skippedCount));
        onUnselectItems();
        refetch();
      } catch (error) {
        notify(error.message || 'Failed to mark rows as paid.', 'warning');
      } finally {
        setIsSaving(false);
        prompt.close();
      }
    },
  );

  if (!hasPermission(permission)) return null;

  const hasDateRange = isDateRangeSet(filterValues, dateRange);
  const hasSelection = (selectedIds?.length ?? 0) > 0;
  const isBusy = prompt.isLoading || isSaving;
  const isDisabled = isBusy || !hasDateRange || !hasSelection;

  return (
    <Fragment>
      <Tooltip
        open={isTooltipOpen}
        onClose={() => setIsTooltipOpen(false)}
        onOpen={hasDateRange ? undefined : () => setIsTooltipOpen(true)}
        title={
          <Box>
            {tooltipLines.map((line, index) => (
              <Fragment key={line}>
                {index > 0 && <br />}
                <Typography variant="caption">{line}</Typography>
              </Fragment>
            ))}
          </Box>
        }
      >
        <span>
          <Button
            color="primary"
            disabled={isDisabled}
            onClick={() => prompt.open(() => buildBody(selectedIds))}
          >
            {isBusy ? <CircularProgress size={16} /> : label}
          </Button>
        </span>
      </Tooltip>
      <MarkAsPaidConfirm
        {...prompt.dialogProps}
        loading={isSaving}
        title={label}
      />
    </Fragment>
  );
};

export default MarkAsPaidButton;
